import React, { useState, useEffect, useContext, useRef } from "react";

import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import helpers from "../../services/helpers";
import { httpRequest } from "../../services/httpService";
import ProfileContext from "../../services/profileContext";
import store from "../../services/store";

export default function ProductLookup({
    mode = "default",
    is_show = false,
    setShow,
    handleSelect,
    exclude = [],
    restrict_stock = true,
    show_price = true,
    show_cost = false,
    show_bundle = true,
    module = "",
    module_ref = false,
    warehouse_id = 0,
    buttonSelectText = "Select"
}) {
    const { account } = useContext(ProfileContext);
    const default_currency = helpers.getSettingValue(
        account?.settings,
        "default_currency",
        "₱"
    );
    const searchRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState({
        search: "",
        product_type: 0
    });
    const [selectedProduct, setSelectedProduct] = useState(null);

    const handleFilterChange = (e) => {

        const { name, value } = e.target;

        setFilters(prev => ({
            ...prev,
            [name]: value
        }));

    };

    useEffect(() => {
        if (is_show) {
            setTimeout(() => {
                searchRef.current?.focus();
            }, 100);
        }
    }, [is_show]);

    useEffect(() => {

        if (!is_show) return;

        const keyword = filters.search.trim();

        if (!keyword) {
            setProducts([]);
            return;
        }

        const timer = setTimeout(() => {
            fetchData();
        }, 300);

        return () => clearTimeout(timer);

    }, [
        filters.search,
        filters.product_type,
        is_show
    ]);

    useEffect(() => {
        if (products.length) {
            setSelectedProduct(products[0]);
        }
        else{
            setSelectedProduct(null);
        }
    }, [products]);

    function fetchData(){

        let filter = `?store_id=${helpers.getStoreID()}&`

        // if(value){
        //     filter += 'search='+value+'&'
        // }
        if(filters.search){
            filter += 'search='+filters.search+'&'
        }
        if(parseInt(filters.product_type) !== 0){
            filter += 'product_type='+filters.product_type+'&'
        }

        if(module){
            filter += 'module='+module+'&'
        }
        if(module_ref){
            filter += 'module_ref='+module_ref+'&'
        }
        if(warehouse_id){
            filter += 'warehouse_id='+warehouse_id+'&'
        }

        if(!show_bundle){
            filter += 'exclude_type=bundle&'
        }
        
        filter = filter.slice(0, -1)

        const url = helpers.createUrl(`V1/manage/item/search${filter}`)

        httpRequest(url, "GET", {}, true)
            .then((res) => {
                const productResult = res.data ?? []

                const excludedIds = new Set(exclude.map(item => item.product_variant_id));

                const productList = productResult.filter(product => !excludedIds.has(product.product_variant_id));
                setProducts(productList);
            })
            .catch(() => {
                setProducts([]);
                setLoading(0)
            }); 
    }

    const productTemplate = (row) => {

        return (
            <div>
                <div className="font-weight-bold">
                    {row.product_name} ({row.product_variant_name})
                </div>

                <div className="text-muted small">
                    Product Code:
                    <span className="font-weight-bold ml-1">
                        {row.product_code}
                    </span>
                </div>

                <div className="text-muted small">
                    Category:
                    <span className="ml-1">
                        {row.category_name ?? "-"}
                    </span>

                    <span className="mx-2">|</span>

                    Brand:
                    <span className="ml-1">
                        {row.brand_name ?? "-"}
                    </span>
                </div>
            </div>
        );
    };

    const stockTemplate = (row) => {
        const qty = Number(row.available_stock);

        return (
            <Tag
                value={qty}
                severity={qty > 0 ? "success" : "danger"}
            />
        );
    };

    const priceTemplate = (row) =>
        helpers.formatCurrencyAmount(
        row.product_price,
        default_currency
    );

    const costTemplate = (row) =>
        helpers.formatCurrencyAmount(
        row.product_cost,
        default_currency
    );

    const featuredTemplate = (row) => (
        row.is_featured ? (
            <i className="fas fa-check text-success" title="Featured"></i>
        ) : (
            <i className="fas fa-times text-muted" title="Not Featured"></i>
        )
    );

    const actionTemplate = (row) => {

        // Featured Product Mode
        if (mode === "featured-product") {

            return row.is_featured ? (

                <Button
                    label="Remove Featured"
                    icon="fas fa-minus"
                    className="btn btn-danger btn-sm p-button-sm"
                    onClick={() => onSelect(row)}
                />

            ) : (

                <Button
                    label="Add to Featured"
                    icon="fas fa-plus"
                    className="btn btn-primary btn-sm p-button-sm"
                    onClick={() => onSelect(row)}
                />

            );

        }

        // Restrict Stock
        if (
            restrict_stock &&
            Number(row.available_stock) <= 0 &&
            row.track_inventory == 1
        ) {

            return (
                <span className="text-danger">
                    Out of Stock
                </span>
            );

        }

        // Default Select
        return (

            <Button
                label={buttonSelectText}
                icon="fas fa-check"
                className="btn btn-primary btn-sm p-button-sm"
                onClick={() => onSelect(row)}
            />

        );

    };

    function onSelect(e){
        if(mode == 'featured-product'){
            handleSelect(e)

            const productVariantId = e.product_variant_id;
            const isFeatured = e.is_featured ? 0 : 1;

            setProducts(prev =>
                prev.map(product =>
                    product.product_variant_id === productVariantId
                        ? {
                            ...product,
                            is_featured: isFeatured
                        }
                        : product
                )
            );
        }
        else{
    
            setFilters(prev => ({
                ...prev,
                search: ""
            }));

            setProducts([]);
            //reset();
            setShow(0);
            handleSelect(e)
        }
    }

    const handleSearchKeyDown = (e) => {

        if (!products.length) return;

        const currentIndex = products.findIndex(
            p => p.product_variant_id === selectedProduct?.product_variant_id
        );

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                if (currentIndex < products.length - 1) {
                    setSelectedProduct(
                        products[currentIndex + 1]
                    );
                }
                break;
            case "ArrowUp":
                e.preventDefault();
                if (currentIndex > 0) {
                    setSelectedProduct(
                        products[currentIndex - 1]
                    );
                }
                break;
            case "Enter":
                e.preventDefault();
                if (selectedProduct) {
                    onSelect(selectedProduct);
                }
                break;
            default:
                break;
        }
    };

    return (

    <Dialog
        header="Product Lookup"
        visible={is_show}
        style={{ width: "1100px"}}
        modal
        onHide={setShow}
        draggable={false} 
        position="top" 
    >

        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%"
            }}
        >
            <div className="row mt-2">

                <div className="col-md-3 mb-2">
                    <Dropdown
                        name="product_type"
                        value={filters.product_type}
                        options={store.product_types}
                        optionLabel="label"
                        optionValue="id"
                        placeholder="All Item Types"
                        className="w-100"
                        onChange={handleFilterChange}
                        appendTo="self"
                    />

                </div>

                <div className="col-md-9">

                    <InputText
                        ref={searchRef}
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Search product name, product code or barcode..."
                        className="w-100"
                    />
                </div>

            </div>

            <div className="mt-3">

                {!filters.search.trim() ? (

                    <div className="text-center text-muted py-5">

                        <i className="pi pi-search mb-3" style={{fontSize:'40px'}} />

                        <h5>Search for a Product</h5>

                        <p>
                            Start typing a product name,
                            product code or barcode.
                        </p>

                    </div>

                ) : (

                    // <DataTable
                    //     value={products}
                    //     loading={loading}
                    //     responsiveLayout="scroll"
                    //     emptyMessage="No products found."
                    //     stripedRows
                    //     size="small"
                    // >

                    <DataTable
                        value={products}
                        loading={loading}
                        selectionMode="single"
                        selection={selectedProduct}
                        onSelectionChange={(e) => setSelectedProduct(e.value)}
                        onRowDoubleClick={(e)=>onSelect(e.data)}
                        dataKey="product_variant_id"
                        responsiveLayout="scroll"
                        stripedRows
                        size="small"
                        emptyMessage="No products found."
                        rowHover
                        scrollable
                        scrollHeight="60vh"
                    >

                        <Column
                            field="product_variant_id"
                            header="ID"
                            style={{ width: '90px' }}
                        />

                        <Column
                            header="Item"
                            body={productTemplate}
                        />

                        <Column
                            header="Type"
                            body={helpers.getProductType}
                        />

                        <Column
                            header="Stock"
                            body={stockTemplate}
                            bodyStyle={{ textAlign: "left" }}
                            headerStyle={{ textAlign: "left", width: "100px" }}
                        />

                        {show_price && (
                            <Column
                                header="Price"
                                body={priceTemplate}
                                bodyStyle={{ textAlign: "left" }}
                                headerStyle={{ textAlign: "left", width: "120px" }}
                            />
                        )}

                        {show_cost && (
                            <Column
                                header="Cost"
                                body={costTemplate}
                                bodyStyle={{ textAlign: "left" }}
                                headerStyle={{ textAlign: "left", width: "120px" }}
                            />
                        )}

                        <Column
                            header="Featured"
                            body={featuredTemplate}
                            headerStyle={{ width: "100px", textAlign: "left" }}
                            bodyStyle={{ textAlign: "left" }}
                        />

                        <Column
                            body={actionTemplate}
                            headerStyle={{ width: "180px" }}
                            bodyStyle={{ textAlign: "center" }}
                        />

                    </DataTable>

                )}

            </div>
        </div>
    </Dialog>

    );

}